import React from 'react';

import styled from 'styled-components';

import CommunityPositioningLayout from 'common/Widgets/Graph/CommunityPositioningLayout';
import MarketPositioningChart from 'common/Widgets/MarketPositioning/MarketPositioningChart';
import { MarketPositioningPoint } from 'common/Widgets/MarketPositioning/types';
import AiCiSectionIcon from 'components/AiCompetitiveIntelligence/AiCiSectionIcon';
import { SpeedToLeadMetrics } from 'types/recommendations';

import { SPEED_TO_LEAD_SCORE_RANGE } from './constants';
import {
  CommunityMeta,
  CommunityName,
  DashboardCard,
  EmptySection,
  SectionDescription,
  SectionHeading,
  SectionTitle,
} from './SpeedToLeadSection.styles';

const ChartArea = styled.div`
  height: 360px;

  @media (max-width: 850px) {
    height: auto;
  }
`;

const CommunityKeyRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const CommunityDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  margin-top: 4px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

export type SpeedToLeadScoreRow = SpeedToLeadMetrics['quality_vs_speed']['rows'][number] & {
  community_color: string;
};

type Props = {
  primaryCommunityId: number | null;
  scoreRows: SpeedToLeadScoreRow[];
  scoresAvailable: boolean;
};

const CommunityKeyEntry = ({ row }: { row: SpeedToLeadScoreRow }) => (
  <CommunityKeyRow>
    <CommunityDot $color={row.community_color} />
    <div>
      <CommunityName>{row.community_name}</CommunityName>
      <CommunityMeta>
        Speed {row.speed_score}/100 · Quality {row.quality_score}/100
      </CommunityMeta>
    </div>
  </CommunityKeyRow>
);

const SpeedToLeadQualityVsSpeed = ({ primaryCommunityId, scoreRows, scoresAvailable }: Props) => {
  const orderedScoreRows = [
    ...scoreRows.filter((row) => row.community_id !== primaryCommunityId),
    ...scoreRows.filter((row) => row.community_id === primaryCommunityId),
  ];
  const chartRows: MarketPositioningPoint[] = orderedScoreRows.map((row) => ({
    community_id: row.community_id,
    community_name: row.community_name,
    community_color: row.community_color,
    quality_score: row.speed_score as number,
    market_rate: row.quality_score as number,
  }));
  const primaryChartRow = chartRows.find((row) => row.community_id === primaryCommunityId);
  const primaryRow = scoreRows.find((row) => row.community_id === primaryCommunityId);

  return (
    <DashboardCard>
      <SectionHeading>
        <AiCiSectionIcon src="/icons/grain.svg" />
        <div>
          <SectionTitle>Quality vs. Speed</SectionTitle>
          <SectionDescription>
            How each community balances response speed against follow-up quality. Top right is best. Lower left is
            lagging behind your community.
          </SectionDescription>
        </div>
      </SectionHeading>
      {!scoresAvailable || scoreRows.length === 0 ? (
        <EmptySection>Quality and Speed scores are not available for this request group yet.</EmptySection>
      ) : (
        <CommunityPositioningLayout
          chart={
            <ChartArea>
              <MarketPositioningChart
                data={chartRows}
                community={primaryChartRow}
                xAxisTitle="Speed Score"
                yAxisTitle="Quality Score"
                xAxisRange={{ min: SPEED_TO_LEAD_SCORE_RANGE.MIN, max: SPEED_TO_LEAD_SCORE_RANGE.MAX }}
                yAxisRange={{ min: SPEED_TO_LEAD_SCORE_RANGE.MIN, max: SPEED_TO_LEAD_SCORE_RANGE.MAX }}
                xAxisLabel="Your Speed"
                yAxisLabel="Your Quality"
                xAxisFormatter={(value) => String(Math.round(Number(value)))}
                yAxisFormatter={(value) => String(Math.round(Number(value)))}
                xTooltipFormatter={(point) => `Speed: ${Math.round(point.quality_score)}`}
                yTooltipFormatter={(point) => `Quality: ${Math.round(point.market_rate)}`}
                useScoreFormat
              />
            </ChartArea>
          }
          primaryEntry={primaryRow && <CommunityKeyEntry row={primaryRow} />}
          competitorEntries={scoreRows
            .filter((row) => row.community_id !== primaryCommunityId)
            .map((row) => (
              <CommunityKeyEntry key={row.community_id} row={row} />
            ))}
        />
      )}
    </DashboardCard>
  );
};

export default SpeedToLeadQualityVsSpeed;
